import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaChartLine, FaBell, FaUsers, FaFileAlt, FaCertificate, FaCreditCard, FaEye, FaExclamationTriangle, FaEuroSign, FaCalendarAlt } from 'react-icons/fa';
import { adminAPI } from '../../services/api';

const AdminDashboard = () => {
  const { user } = useSelector((state) => state.auth);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newSignups: 0,
    totalLoveshots: 0,
    pendingReports: 0,
    revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, logsResponse] = await Promise.all([
          adminAPI.getStats(),
          adminAPI.getLogs({ limit: 10 })
        ]);

        setStats(statsResponse.data);
        setRecentActivities(logsResponse.data.map(log => ({
          id: log.id,
          type: getActivityType(log.level),
          message: log.message,
          time: log.time
        })));
      } catch (error) {
        console.error('Error fetching admin data:', error);
        // No fallback mock data, show real data (empty if none)
        setRecentActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getActivityType = (level) => {
    switch (level) {
      case 'error': return 'report';
      case 'warning': return 'report';
      case 'info': return 'user';
      default: return 'user';
    }
  };

  // Check if user is admin or superadmin
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  const dashboardItems = [
    {
      icon: FaChartLine,
      title: 'Analytics',
      description: 'Aperçu global et statistiques détaillées.',
      path: '/admin/dashboard',
      color: 'primary'
    },
    {
      icon: FaBell,
      title: 'Notifications',
      description: 'Gérer les notifications système.',
      path: '/admin/notifications',
      color: 'info'
    },
    {
      icon: FaUsers,
      title: 'Utilisateurs',
      description: 'Gérer et modérer les comptes utilisateurs.',
      path: '/admin/users',
      color: 'success'
    },
    {
      icon: FaFileAlt,
      title: 'Contenus',
      description: 'Modérer les loveshots et stories.',
      path: '/admin/content',
      color: 'warning'
    },
    {
      icon: FaCertificate,
      title: 'Certifications',
      description: 'Traiter les demandes de certification.',
      path: '/admin/certifications',
      color: 'secondary'
    },
    {
      icon: FaCreditCard,
      title: 'Paiements',
      description: 'Gérer les transactions et abonnements.',
      path: '/admin/payments',
      color: 'danger'
    }
  ];

  return (
    <div className="admin-dashboard min-vh-100" style={{ backgroundColor: 'var(--chatoo-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h2 mb-1">Tableau de Bord Administrateur</h1>
                <p className="mb-0" style={{ color: 'var(--chatoo-text-secondary)' }}>Bienvenue, {user.name || 'Admin'} • {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="text-end">
                <FaCalendarAlt className="text-muted me-2" />
                <small className="text-muted">Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}</small>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaUsers className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.totalUsers.toLocaleString()}</h4>
                <small style={{ color: 'var(--chatoo-text-secondary)' }}>Utilisateurs Total</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaEye className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.activeUsers.toLocaleString()}</h4>
                <small className="text-muted">Utilisateurs Actifs</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaChartLine className="text-info mb-2" size={24} />
                <h4 className="h5 mb-1">+{stats.newSignups}</h4>
                <small className="text-muted">Nouveaux Aujourd'hui</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaFileAlt className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.totalLoveshots.toLocaleString()}</h4>
                <small className="text-muted">Loveshots</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaExclamationTriangle className="text-danger mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.pendingReports}</h4>
                <small className="text-muted">Signalements</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaEuroSign className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.revenue.toLocaleString()}€</h4>
                <small className="text-muted">Revenus Mensuels</small>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="mb-3">Actions Rapides</h5>
          </div>
          {dashboardItems.map((item, index) => (
            <div key={index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
              <div className="card h-100 shadow-sm border-0 hover-card">
                <div className="card-body text-center">
                  <item.icon size={32} className={`text-${item.color} mb-3`} />
                  <h6 className="card-title mb-2">{item.title}</h6>
                  <p className="card-text text-muted small mb-3">{item.description}</p>
                  <button
                    className={`btn btn-${item.color} btn-sm w-100`}
                    onClick={() => window.location.href = item.path}
                  >
                    Accéder
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activities */}
        <div className="row">
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header border-0">
                <h5 className="mb-0">Activités Récentes</h5>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {recentActivities.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="list-group-item border-0 px-4 py-3">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-3">
                          {activity.type === 'user' && <FaUsers className="text-primary" />}
                          {activity.type === 'report' && <FaExclamationTriangle className="text-danger" />}
                          {activity.type === 'certification' && <FaCertificate className="text-success" />}
                          {activity.type === 'payment' && <FaEuroSign className="text-warning" />}
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1">{activity.message}</p>
                          <small className="text-muted">{activity.time}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                  {recentActivities.length > 5 && (
                    <div className="text-center p-3">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => window.location.href = '/admin/activities'}
                      >
                        Voir plus
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Sidebar */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header border-0">
                <h6 className="mb-0">Aperçu Rapide</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Taux d'engagement</small>
                    <small className="fw-bold">78%</small>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-success" style={{width: '78%'}}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Satisfaction utilisateurs</small>
                    <small className="fw-bold">92%</small>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-info" style={{width: '92%'}}></div>
                  </div>
                </div>
                <div className="mb-0">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Temps de réponse moyen</small>
                    <small className="fw-bold">2.3h</small>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-warning" style={{width: '65%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hover-card:hover {
          transform: translateY(-2px);
          transition: transform 0.2s ease;
        }
        .min-vh-100 {
          min-height: 100vh;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
